<template lang="pug">
.section
  b-collapse(v-bind:open.sync="open")
    .level.is-mobile(slot="trigger")
      .level-left
        .level-item
          .image.is-72x72
            img(v-bind:src="partyAvatarUrl")
        .level-item
          .content
            strong {{ partyName }}
            .icon.has-text-danger(v-if="hasNotes")
              i.fas.fa-comment
            div {{ formattedUncompleted(bookingStatus) }}
            div {{ bookingDateTime }}
      .level-right
        .icon
          i.fas(v-bind:class="angleClass")
    br
    .card
      .card-content
        template(v-if="!isExternalBooking")
          div
            .icon
              i.fas.fa-envelope
            a(v-bind:href="partyEmailHref") {{ partyEmail }}
          div
            .icon
              i.fas.fa-phone
            a(v-bind:href="partyPhoneHref") {{ partyPhone }}
          div
            .icon
              i.fas.fa-location-arrow
            a(v-bind:href="locationAddressHref") {{ locationAddress }}
          br
        template(v-if="hasNotes")
          template(v-for="note in notes")
            div {{ formattedNote(note) }}
            br
        template(v-if="canAccept || canAddNote || canCancel || canReject")
          div.field.is-grouped
            .control(v-if="canReject")
              button.button.is-danger(v-on:click="clickReject") Reject
            .control(v-if="canCancel")
              button.button.is-danger(v-bind:disabled="isProfessional() && isOnlineBooking && !isOnline" v-on:click="clickCancel") Cancel
            .control(v-if="canAccept")
              button.button.is-primary(v-bind:disabled="isOnlineBooking && !isOnline" v-on:click="clickAccept") Accept
            .control(v-if="canAddNote")
              button.button.is-outlined(v-on:click="clickAddNote") Add Note
          div(v-if="isProfessional() && (canAccept || canCancel) && isOnlineBooking && !isOnline") This is an 'Online via Zoom' booking and you have not authorised Zoom. Please #[router-link(to="/zoom") authorise Zoom] if you wish to Accept/Cancel this booking.
  br
</template>

<script>
import { format, parse } from 'date-fns'

import Constants from '@/constants'

import ApiMixin from '@/mixins/ApiMixin.vue'

export default {
  mixins: [
    ApiMixin
  ],
  props: {
    booking: {
      required: true,
      type: Object
    }
  },
  data () {
    return {
      open: false
    }
  },
  computed: {
    angleClass () {
      return this.open ? 'fa-angle-up' : 'fa-angle-down'
    },
    bookingDateTime () {
      return `${this.formattedDateTime(this.booking.booking_start)} - ${this.formattedTime(this.booking.booking_end)}`
    },
    bookingStatus () {
      return this.booking.booking_status
    },
    canAccept () {
      return Constants.USER_TYPE === Constants.USER_TYPE_PROFESSIONAL && this.booking.booking_status === Constants.STATUS_REQUESTED
    },
    canAddNote () {
      return Constants.USER_TYPE === Constants.USER_TYPE_PROFESSIONAL && (this.booking.booking_status === Constants.STATUS_ACCEPTED || this.booking.booking_status === Constants.STATUS_REQUESTED)
    },
    canCancel () {
      if (Constants.USER_TYPE === Constants.USER_TYPE_CLIENT) {
        return this.booking.booking_status === Constants.STATUS_ACCEPTED || this.booking.booking_status === Constants.STATUS_REQUESTED
      } else {
        return this.booking.booking_status === Constants.STATUS_ACCEPTED
      }
    },
    canReject () {
      return Constants.USER_TYPE === Constants.USER_TYPE_PROFESSIONAL && this.booking.booking_status === Constants.STATUS_REQUESTED
    },
    hasNotes () {
      return this.notes.length > 0
    },
    isExternalBooking () {
      return this.booking.client_id === Constants.EXTERNAL_CLIENT_ID
    },
    isOnline () {
      return this.zoomHasTokens !== null
    },
    isOnlineBooking () {
      return this.locationAddress === 'Online via Zoom'
    },
    location () {
      return this.apiLocations.find((location) => location.id === this.booking.location_id)
    },
    locationAddress () {
      return this.location ? this.location.address : null
    },
    locationName () {
      return this.location ? this.location.name : null
    },
    locationAddressHref () {
      return this.location && this.location.lat && this.location.lng ? `https://www.google.com/maps/search/?api=1&query=${this.location.lat},${this.location.lng}` : null
    },
    notes () {
      return this.apiNotes.filter((note) => {
        return this.booking.notes.includes(note.id)
      })
    },
    party () {
      if (Constants.USER_TYPE === Constants.USER_TYPE_CLIENT) {
        return this.apiProfessionals.find((professional) => professional.id === this.booking.professional_id)
      } else {
        if (this.isExternalBooking) {
          return this.apiProfessionals.find((professional) => professional.id === this.apiUserId)
        } else {
          return this.apiClients.find((client) => client.id === this.booking.client_id)
        }
      }
    },
    partyAvatarUrl () {
      return this.party && this.party.avatar ? this.party.avatar.url : 'png/user.png'
    },
    partyEmail () {
      return this.party ? this.party.email : null
    },
    partyEmailHref () {
      return this.partyEmail ? `mailto:${this.partyEmail}` : null
    },
    partyName () {
      return this.isExternalBooking ? 'External Booking' : this.party ? this.party.name : null
    },
    partyPhone () {
      return this.party ? this.party.phone : null
    },
    partyPhoneHref () {
      return this.partyPhone ? `tel:${this.partyPhone}` : null
    }
  },
  methods: {
    async acceptBooking () {
      if (this.isOnlineBooking) {
        await this.scheduleMeeting()
      }
      await this.apiUpdateBooking(this.booking.professional_id, this.booking.id, 'Accepted')
    },
    async addNote (note) {
      await this.apiCreateBookingNote(this.booking.professional_id, this.booking.id, note)
      await this.apiGetBookings()
    },
    async cancelBooking () {
      if (this.isProfessional() && this.isOnlineBooking) {
        await this.unscheduleMeeting()
      }
      await this.apiUpdateBooking(this.booking.professional_id, this.booking.id, Constants.STATUS_CANCELLED)
    },
    clickAccept () {
      this.acceptBooking()
    },
    clickAddNote () {
      this.$buefy.dialog.prompt({
        cancelText: 'Cancel',
        confirmText: 'OK',
        inputAttrs: {
          placeholder: 'Note'
        },
        message: 'What do you want to say?',
        onConfirm: (note) => this.addNote(note),
        title: 'Add Note',
        type: 'is-primary'
      })
    },
    clickCancel () {
      this.$buefy.dialog.confirm({
        cancelText: 'No',
        confirmText: 'Yes',
        focusOn: 'cancel',
        hasIcon: true,
        message: 'Are you sure you want to cancel this booking?',
        onConfirm: () => this.cancelBooking(),
        title: 'Cancel Booking',
        type: 'is-danger'
      })
    },
    clickReject () {
      this.$buefy.dialog.prompt({
        cancelText: 'No',
        confirmText: 'Yes',
        hasIcon: true,
        inputAttrs: {
          placeholder: 'Optional Note',
          required: false
        },
        message: 'Are you sure you want to reject this booking?',
        onConfirm: (note) => this.rejectBooking(note),
        title: 'Reject Booking',
        type: 'is-danger'
      })
    },
    formattedDateTime (dateTime) {
      return format(dateTime, 'ddd D MMM HH:mm')
    },
    formattedNote (note) {
      const formattedDescription = this.formattedUncompleted(note.description)
      return `${this.formattedDateTime(note.created_at)}: ${formattedDescription}`
    },
    formattedTime (time) {
      return format(time, 'HH:mm')
    },
    formattedUncompleted (string) {
      return string ? string.replace('Uncompleted/Client', `Not attended by ${this.$t('client')}`).replace('Uncompleted/Professional', `Not attended by ${this.$t('professional')}`) : string
    },
    async rejectBooking (note) {
      if (note) {
        await this.apiCreateBookingNote(this.booking.professional_id, this.booking.id, note)
      }
      await this.apiUpdateBooking(this.booking.professional_id, this.booking.id, 'Rejected')
    },
    async scheduleMeeting () {
      const startTime = parse(this.booking.booking_start, 'YYYY-MM-DD HH:mm:ss')
      const topic = `${this.locationName}, delivered by: ${this.booking.professional_name}, delivered to: ${this.booking.client_name}.`
      const meeting = await this.zoomPostUri('https://api.zoom.us/v2/users/me/meetings', { start_time: startTime, timezone: 'UTC', topic })
      const noteJoinUrl = meeting.data.join_url
      const noteStartDate = format(startTime, 'Do MMMM YYYY')
      const noteStartTime = format(startTime, 'HH:mm')
      const note = `Zoom meeting scheduled for ${noteStartDate} at ${noteStartTime}; join ${noteJoinUrl}.`
      await this.addNote(note)
    },
    async unscheduleMeeting () {
      const zoomMeetings = await this.zoomGetMeetings()
      const meetings = zoomMeetings.data.meetings
      const startTime = parse(this.booking.booking_start, 'YYYY-MM-DD HH:mm').toISOString().replace('.000Z', 'Z')
      const topic = `${this.locationName}, delivered by: ${this.booking.professional_name}, delivered to: ${this.booking.client_name}.`
      const meeting = meetings.find((meeting) => meeting.start_time === startTime && meeting.topic === topic)

      if (meeting) {
        await this.zoomDeleteUri(`https://api.zoom.us/v2/meetings/${meeting.id}`)
        await this.addNote('Zoom meeting unscheduled.')
      }
    }
  }
}
</script>
